<template>

    <v-container fluid class="pa-3">

        <v-card>

            <material-header
                :title="pageTitle"
                :info="data.name"
                icon="mdi-sitemap">

                <template slot="buttons">

                    <v-btn v-if="editing"
                           class="ml-3" color="primary"
                           :loading="saving"
                           @click="saveChanges(false)">Save
                    </v-btn>

                    <v-btn v-if="!editing"
                           class="ml-3" color="primary"
                           :loading="saving"
                           @click="saveChanges">Create
                    </v-btn>

                    <v-tooltip bottom color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" fab small class="ml-4 elevation-2" color="red" dark @click="close">
                                <v-icon> mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Exit editor</span>
                    </v-tooltip>

                </template>

            </material-header>

            <validation-observer ref="validationObserver" v-slot="{ invalid, errors }">

                <v-tabs
                    slider-color="primary"
                    color="white"
                    v-if="hasPermission('run queries')"
                    v-model="activeTab">

                    <v-tab class="ml-2" key="tab1">
                        BASIC SETTINGS
                        <v-chip color="error"
                                class="ml-1"
                                small
                                v-if="errors.settings && errors.settings.length > 0">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab class="ml-2" key="tab2">
                        REGISTRATION SURVEY
                    </v-tab>

                    <v-tab class="ml-2" key="tab3">
                        SECURITY
                    </v-tab>

                    <v-tab v-if="data.id" class="ml-2" key="tab4">
                        API KEYS
                    </v-tab>

                </v-tabs>

                <v-tabs-items v-model="activeTab">

                    <!-- SETTINGS -->
                    <v-tab-item key="tab1"
                                reverse-transition="fade-transition"
                                transition="fade-transition">

                        <settings-tab
                            :sub-panel="data"
                            @change="settingsTabChanged"
                            @delete="deleteClicked"
                        ></settings-tab>

                    </v-tab-item>

                    <v-tab-item key="tab2"
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <registration-survey-tab :sub-panel="data"
                                                 @change="registrationSurveyChanged"></registration-survey-tab>
                    </v-tab-item>

                    <!-- SECURITY TAB -->
                    <v-tab-item key="tab3"
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <security-tab
                            :sub-panel="data"
                            @change="securityTabChanged"
                        ></security-tab>
                    </v-tab-item>

                    <!-- API KEYS -->
                    <v-tab-item key="tab4"
                                reverse-transition="fade-transition"
                                transition="fade-transition"
                                v-if="data.id">
                        <api-tab :sub-panel="data"></api-tab>
                    </v-tab-item>

                </v-tabs-items>

            </validation-observer>

            <json v-if="false" :data="data"></json>

        </v-card>
    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import {mapActions, mapGetters, mapState} from 'vuex';
import ToolbarPage from '../../../components/UI/ToolbarPage';
import SettingsTab from './Tabs/SettingsTab';
import TitleCard from "../../../components/UI/TitleCard";
import SecurityTab from "./Tabs/SecurityTab";
import ApiTab from "./Tabs/ApiTab";
import RegistrationSurveyTab from "./Tabs/RegistrationSurveyTab";

export default {

    components: {
        ToolbarPage,
        SettingsTab,
        SecurityTab,
        MaterialHeader,
        Json,
        TitleCard,
        ApiTab,
        RegistrationSurveyTab
    },

    data() {
        return {

            pageTitle: null,
            activeTab: null,
            survey: null,

            // Default for new sub panels
            data: {
                name: '',
                locales: [],
                terminate_proxies: true,
                terminate_fingerprint: true,
                terminate_ip: true,
                terminate_country: true,
                terminate_bots: true,
                terminate_hosted: true,
            },

            errorMessages: {},
        }
    },

    computed: {

        ...mapState('SubPanels', [
            'saving'
        ]),
        ...mapGetters('SubPanels', [
            'findById'
        ]),

        editing() {
            return this.$route.params.id;
        },
    },

    created() {
        if (this.editing) {
            // This is here in case user reloads the page
            this.loadSubPanels()
                .then(() => {
                    this.getSubPanelFromStore();
                });
            this.pageTitle = "Edit sub panel";

        } else {
            this.pageTitle = "Create new sub panel";
        }
    },

    methods: {

        ...mapActions('SubPanels', [
            'loadSubPanels',
            'createSubPanel',
            'updateSubPanel',
            'deleteSubPanel',
            'saveRegistrationSurvey'
        ]),

        getSubPanelFromStore() {
            if (this.$route.params.id) {
                this.data = this.findById(this.$route.params.id);
            }
        },

        settingsTabChanged(data) {
            // Merge changes from settings tab
            this.data = Object.assign({}, this.data, data);
        },

        securityTabChanged(data) {
            this.data = Object.assign(this.data, data);
        },

        registrationSurveyChanged(data) {
            this.survey = data;
        },

        saveChanges(exit = true) {

            this.$refs.validationObserver.validate()
                .then(isValid => {
                    if (!isValid) {
                        const errors = Object.values(this.$refs.validationObserver.errors);
                        const firstError = errors.find(error => error.length > 0)[0];

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: firstError
                        });
                    } else {
                        if (this.data.id) this.updateSubPanelDo(exit); else this.createSubPanelDo(exit);
                    }
                });
        },

        handleRegistrationSurvey(subPanel) {

            if (this.survey) {

                this.saveRegistrationSurvey({id: subPanel.id, data: this.survey})
                    .catch((error) => {

                        console.error(error);

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'OOPS: Registration survey failed to save!'
                        });
                    });
            }
        },

        updateSubPanelDo(exit) {

            this.updateSubPanel(this.data)
                .then((response) => {
                    this.handleRegistrationSurvey(response.data.subPanel);
                    Bus.$emit('flash-message', {message: 'Sub Panel updated...'});
                    if (exit) this.$router.go(-1);
                })
                .catch((error) => {

                    if (error.response.status === 422) {

                        const errors = Object.entries(error.response.data.errors);

                        const firstError = errors[0][1][0];
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: firstError
                        });
                    } else {

                        console.error(error);

                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'OOPS: Sub Panel failed to update!'
                        });
                    }
                });
        },

        createSubPanelDo(exit) {

            this.createSubPanel(this.data)
                .then((response) => {
                    this.handleRegistrationSurvey(response.data.subPanel);
                    Bus.$emit('flash-message', {message: 'Sub Panel created...'});

                    if (exit) this.$router.go(-1);
                })
                .catch((response) => {
                    Bus.$emit('flash-message', {
                        type: 'error', message: 'OOPS: Sub Panel failed to create!'
                    });
                });
        },

        deleteClicked(id) {

            this.deleteSubPanel(id)
                .then(() => {
                    this.$router.push({name: 'subpanels-list'});
                    Bus.$emit('flash-message', {message: 'Sub panel successfully deleted!'});
                })
                .catch((response) => {
                    if (response.status === 406) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Delete not possible. One sub panel must always exist!'
                        });
                    } else if (response.status === 409) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'This sub panel contains members and cannot be deleted!'
                        });
                    } else {
                        Bus.$emit('flash-message', {type: 'error', message: 'Oops! Sub panel failed to delete!'});
                    }
                });
        },

        close() {
            this.$router.push({name: 'subpanels-list'});
        }
    }
}
</script>
